import React, { useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
  getDocs,
  query,
  limit,
  collection,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import "bootstrap-icons/font/bootstrap-icons.css";
import OtpVerifyModal from "./OtpVerifyModal";
import TotpVerifyModal from "./TotpVerifyModal";
import {
  userDoc,
  userOrgsCol,
  orgMemberDoc,
  userOrgDoc,
} from "../utils/firePaths";

const DEFAULT_AVATAR = "/img/avatar2.jpg";
const LAST_ACTIVE_KEY = "aa:lastActiveAt";

const emailLower = (e) => (e || "").trim().toLowerCase();

const ensureUserAndOrg = async (fbUser) => {
  const uid = fbUser.uid;
  const uref = userDoc(uid);

  // 1) upsert user profile
  const snap1 = await getDoc(uref);

  if (!snap1.exists()) {
    const [name, ...rest] = (fbUser.displayName || "").split(" ");
    await setDoc(
      uref,
      {
        emailLower: emailLower(fbUser.email),
        email: fbUser.email || "",
        nombre: name || "",
        apellido: rest.join(" "),
        avatarUrl: fbUser.photoURL || DEFAULT_AVATAR,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      },
      { merge: true },
    );
  } else {
    await updateDoc(uref, {
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // ✅ 2) leer FRESCO para defaultOrgId
  const snap2 = await getDoc(uref);
  const udata = snap2.exists() ? snap2.data() || {} : {};
  if (udata.defaultOrgId) return udata.defaultOrgId;

  // 3) si ya tiene orgs por índice, usa la primera
  const orgsSnap = await getDocs(query(userOrgsCol(uid), limit(1)));
  if (!orgsSnap.empty) {
    const orgId = orgsSnap.docs[0].id;
    await updateDoc(uref, {
      defaultOrgId: orgId,
      updatedAt: serverTimestamp(),
    });
    return orgId;
  }

  // 4) crear org nueva (primera vez)
  const newOrgRef = doc(collection(db, "orgs"));
  await setDoc(newOrgRef, {
    name: "Mi Empresa",
    ownerUid: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await setDoc(orgMemberDoc(newOrgRef.id, uid), {
    uid,
    role: "Administrador",
    status: "active",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await setDoc(userOrgDoc(uid, newOrgRef.id), {
    orgId: newOrgRef.id,
    role: "Administrador",
    status: "active",
    createdAt: serverTimestamp(),
  });

  await updateDoc(uref, {
    defaultOrgId: newOrgRef.id,
    updatedAt: serverTimestamp(),
  });

  return newOrgRef.id;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const [keepSession, setKeepSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpPhone, setOtpPhone] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const otpResolverRef = useRef(null);
  const resendSmsRef = useRef(null);
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpError, setTotpError] = useState("");
  const [totpLoading, setTotpLoading] = useState(false);
  const totpResolverRef = useRef(null);
  const navigate = useNavigate();
  const { user, loading, setAuthInProgress } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // Esta función es la que el "while" espera para obtener el código del modal
  const askOtpCode = () => {
    return new Promise((resolve) => {
      otpResolverRef.current = resolve;
    });
  };

  const askTotpCode = () => {
    setTotpOpen(true);
    return new Promise((resolve) => {
      totpResolverRef.current = resolve;
    });
  };

  /* ───────── Lógica MFA SMS ───────── */
  const resolveMfaWithSms = async (resolver, phoneHint) => {
    if (window.__recaptchaVerifier && window.__recaptchaVerifier.clear) {
      try {
        await window.__recaptchaVerifier.clear();
      } catch {}
    }

    let div = document.getElementById("recaptcha-container-login");
    if (div) div.remove();
    div = document.createElement("div");
    div.id = "recaptcha-container-login";
    div.style.position = "fixed";
    div.style.left = "-9999px";
    document.body.appendChild(div);

    const verifier = new RecaptchaVerifier(auth, "recaptcha-container-login", {
      size: "invisible",
    });
    await verifier.render();

    const provider = new PhoneAuthProvider(auth);
    let vId = await provider.verifyPhoneNumber(
      { multiFactorHint: phoneHint, session: resolver.session },
      verifier,
    );

    resendSmsRef.current = async () => {
      vId = await provider.verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        verifier,
      );
    };

    while (true) {
      setOtpPhone(phoneHint.phoneNumber);
      setOtpOpen(true);

      const code = await askOtpCode();
      if (!code) throw new Error("Operación cancelada por el usuario.");

      try {
        setOtpLoading(true);
        setOtpError("");
        const cred = PhoneAuthProvider.credential(vId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(cred);
        const userCred = await resolver.resolveSignIn(assertion);
        setOtpOpen(false);
        setOtpLoading(false);
        return userCred.user;
      } catch (err) {
        setOtpLoading(false);
        if (err.code === "auth/invalid-verification-code") {
          setOtpError("Código incorrecto");
        } else {
          throw err;
        }
      }
    }
  };

  /* ───────── Lógica MFA App (TOTP) ───────── */
  const resolveMfaWithTotp = async (resolver, totpHint) => {
    while (true) {
      const code = await askTotpCode();
      if (!code) throw new Error("Operación cancelada por el usuario.");

      try {
        setTotpLoading(true);
        setTotpError("");

        const assertion = TotpMultiFactorGenerator.assertionForSignIn(
          totpHint.uid,
          code,
        );

        const userCred = await resolver.resolveSignIn(assertion);

        setTotpOpen(false);
        setTotpLoading(false);
        return userCred.user;
      } catch (err) {
        setTotpLoading(false);

        if (err.code === "auth/invalid-verification-code") {
          setTotpError("Código incorrecto");
          // se queda el modal abierto y reintenta
        } else {
          throw err;
        }
      }
    }
  };

  const resolveMfa = async (error) => {
    const resolver = getMultiFactorResolver(auth, error);
    const hints = resolver.hints || [];
    const totpHint = hints.find((h) => h.factorId === "totp");
    const phoneHint = hints.find((h) => h.factorId === "phone");

    if (totpHint && phoneHint) {
      const { value } = await Swal.fire({
        title: "Elige tu método 2FA",
        input: "radio",
        inputOptions: {
          totp: "App (Authenticator)",
          phone: `SMS (${phoneHint.phoneNumber})`,
        },
        inputValidator: (v) => (!v ? "Selecciona una opción." : undefined),
        confirmButtonText: "Continuar",
        confirmButtonColor: "#0A6CA9",
      });
      if (value === "totp") return await resolveMfaWithTotp(resolver, totpHint);
      return await resolveMfaWithSms(resolver, phoneHint);
    }
    if (totpHint) return await resolveMfaWithTotp(resolver, totpHint);
    if (phoneHint) return await resolveMfaWithSms(resolver, phoneHint);
    throw error;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !pass)
      return Swal.fire(
        "Campos obligatorios",
        "Completa todos los campos",
        "error",
      );

    try {
      setSubmitting(true);
      await setPersistence(
        auth,
        keepSession ? browserLocalPersistence : browserSessionPersistence,
      );
      const emailNorm = emailLower(email);
      const { user: fbUser } = await signInWithEmailAndPassword(
        auth,
        emailNorm,
        pass,
      );
      await ensureUserAndOrg(fbUser);
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      navigate("/", { replace: true });
    } catch (err) {
      if (err?.code === "auth/multi-factor-auth-required") {
        setAuthInProgress(true);
        try {
          const verifiedUser = await resolveMfa(err);
          await updateDoc(userDoc(verifiedUser.uid), {
            mfaVerified: true,
            lastMfaVerifiedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          await ensureUserAndOrg(verifiedUser);
          localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
          navigate("/", { replace: true });
        } catch (e2) {
          if (e2.message !== "Operación cancelada por el usuario.")
            Swal.fire("Verificación", e2.message || "Error en 2FA", "error");
        } finally {
          setAuthInProgress(false);
        }
      } else {
        const msgs = {
          "auth/invalid-credential": "Correo o contraseña incorrectos.",
          "auth/user-disabled": "Cuenta deshabilitada.",
        };
        Swal.fire("Error", msgs[err.code] || "Problema inesperado.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const socialLogin = async (network) => {
    let provider;
    if (network === "google") {
      provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
    } else if (network === "facebook") {
      provider = new FacebookAuthProvider();
    } else if (network === "apple") {
      provider = new OAuthProvider("apple.com");
    } else return;

    try {
      setSubmitting(true);
      const { user: fbUser } = await signInWithPopup(auth, provider);
      await ensureUserAndOrg(fbUser);
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      navigate("/", { replace: true });
    } catch (err) {
      Swal.fire(`Error con ${network}`, err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="image-social-icons">
          <a href="https://facebook.com" target="_blank" rel="noreferrer">
            <i className="bi bi-facebook" />
          </a>
          <a href="https://instagram.com" target="_blank" rel="noreferrer">
            <i className="bi bi-instagram" />
          </a>
          <a href="https://twitter.com" target="_blank" rel="noreferrer">
            <i className="bi bi-twitter" />
          </a>
        </div>
      </div>

      <div className="login-form">
        <div className="form-wrapper">
          <div className="logo-container">
            <img src="/img/logoCo.png" alt="Logo" className="logo-login" />
          </div>
          <h1>¡Bienvenido!</h1>
          <p className="login-description">Ingresa tu correo y contraseña</p>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <div className="input-container">
                <i className="bi bi-envelope input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Correo electrónico"
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-container">
                <i className="bi bi-key input-icon" />
                <input
                  type={show ? "text" : "password"}
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="Contraseña"
                  disabled={submitting}
                />
                <i
                  className={`bi ${show ? "bi-eye-slash" : "bi-eye"} toggle-password`}
                  onClick={() => setShow(!show)}
                />
              </div>
            </div>
            <div className="form-group keep-session">
              <input
                type="checkbox"
                id="keepSession"
                checked={keepSession}
                onChange={(e) => setKeepSession(e.target.checked)}
                disabled={submitting}
              />
              <label htmlFor="keepSession">Mantener sesión iniciada</label>
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={submitting}
            >
              Iniciar Sesión
            </button>
          {/*
            <div className="forgot-password">
              <a href="/reset-password">¿Olvidaste tu contraseña?</a>
            </div>
            */}
          </form>
          <div className="social-login">
            <button
              onClick={() => socialLogin("google")}
              className="social-btn"
            >
              <img src="/img/gmail.svg" alt="Google" />
            </button>
            <button onClick={() => socialLogin("apple")} className="social-btn">
              <img src="/img/apple.svg" alt="Apple" />
            </button>
            <button
              onClick={() => socialLogin("facebook")}
              className="social-btn"
            >
              <img src="/img/facebook.svg" alt="Facebook" />
            </button>
          </div>
           {/*
          <div className="register-prompt">
            <p>
              ¿No tienes cuenta? <a href="/registro">Regístrate</a>
            </p>
          </div>
          */}
        </div>
      </div>

      {/* AQUÍ ESTÁ EL CAMBIO: No pasamos otpValue ni setOtpValue para no romper el estado interno del modal */}
      <OtpVerifyModal
        isOpen={otpOpen}
        phoneE164={otpPhone}
        loading={otpLoading}
        errorText={otpError}
        onClose={() => {
          setOtpOpen(false);
          otpResolverRef.current?.(null);
        }}
        onSubmitCode={(code) => {
          otpResolverRef.current?.(code);
        }}
        onResend={async () => {
          if (resendSmsRef.current) await resendSmsRef.current();
        }}
      />

      <TotpVerifyModal
        isOpen={totpOpen}
        loading={totpLoading}
        errorText={totpError}
        onClose={() => {
          setTotpOpen(false);
          totpResolverRef.current?.(null);
          totpResolverRef.current = null;
          setTotpError("");
        }}
        onSubmitCode={(code) => {
          totpResolverRef.current?.(code);
          totpResolverRef.current = null;
        }}
      />
    </div>
  );
};

export default Login;
